import { ICoveoSearchConfig } from 'Feature/Coveo/Models/CoveoModels';

declare global {
  // Allows tests to short-circuit remote calls by providing mock data
  // eslint-disable-next-line no-var
  var __mockCoveoConfig: ICoveoSearchConfig | null | undefined;
  // eslint-disable-next-line no-var
  var __mockCoveoToken: string | null | undefined;
}

function getTestCoveoConfigOverride(): ICoveoSearchConfig | null {
  if (process.env.NODE_ENV !== 'test') {
    return null;
  }
  if (typeof globalThis === 'undefined') {
    return null;
  }
  const override = (globalThis as typeof globalThis & { __mockCoveoConfig?: ICoveoSearchConfig | null }).__mockCoveoConfig;
  return override ?? null;
}

function getTestCoveoTokenOverride(): string {
  if (process.env.NODE_ENV !== 'test') {
    return '';
  }
  if (typeof globalThis === 'undefined') {
    return '';
  }
  return ((globalThis as typeof globalThis & { __mockCoveoToken?: string | null }).__mockCoveoToken ?? '') as string;
}

const SearchTokenLocalStorageName: string = 'coveo_token';
export async function GetFreshCoveoToken(): Promise<string> {
  const testToken = getTestCoveoTokenOverride();
  if (testToken) {
    sessionStorage.setItem(SearchTokenLocalStorageName, testToken);
    return testToken;
  }
  const url: string = '/coveo/rest/token';
  let token: string = '';
  try {
    const res = await fetch(url, { method: 'GET' });
    if (res && res.headers) {
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        const data = await res.json();
        if (data['token']) token = data.token;
      } else {
        throw await res.text();
      }
    }
  } catch {
    console.error('Get Coveo Token failed');
  }
  if (token) sessionStorage.setItem(SearchTokenLocalStorageName, token);

  return token;
}

export async function GetCoveoToken(): Promise<string> {
  let token: string = sessionStorage.getItem(SearchTokenLocalStorageName) ?? '';
  if (token === '' || token === null) {
    token = await GetFreshCoveoToken();
  }
  return token;
}

export async function GetCoveoConfig(): Promise<ICoveoSearchConfig | null> {
  const testOverride = getTestCoveoConfigOverride();
  if (testOverride) {
    return testOverride;
  }
  const SearchConfigLocalStorageName: string = 'coveo_config';
  let searchConfigStorage: string = sessionStorage.getItem(SearchConfigLocalStorageName) ?? '';
  if (searchConfigStorage !== '') {
    try {
      const config: ICoveoSearchConfig | null = JSON.parse(searchConfigStorage);
      if (isValidSearchConfig(config)) {
        return config;
      }
    } catch {} // Ignore json parse errors and just get from sitecore
  }
  // Not found in storage, get from Sitecore
  const config: ICoveoSearchConfig | null = await GetCoveoConfigFromSitecore();
  if (isValidSearchConfig(config)) {
    sessionStorage.setItem(SearchConfigLocalStorageName, JSON.stringify(config));
    return config;
  }
  return null;
}
function isValidSearchConfig(config: ICoveoSearchConfig | null) {
  if (config?.organizationId && config.organizationId !== '') {
    return true;
  }
  return false;
}
async function GetCoveoConfigFromSitecore(): Promise<ICoveoSearchConfig | null> {
  const url: string = '/api/sitecore/coveoasync/getcoveoconfig';
  let config: ICoveoSearchConfig | null = null;
  try {
    await fetch(url, {
      method: 'GET',
    })
      .then((res) => res.json())
      .then(
        (result) => {
          try {
            if (result['organizationId'] && result['siteName'] && result['searchSource']) {
              config = result;
            }
          } catch (err: any) {
            console.error(`${'GetCoveoConfig'}() >> ${err.message}`);
          }
        },
        (error) => {
          console.error(`${'GetCoveoConfig'}() >> ${error}`);
        },
      );
  } catch {}
  return config;
}
